import React, { useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import CommonAggridClientSideTable from '../../../common/AgGrid/CommonAggridClientSideTable.jsx';
import WorkQueueNameCell from '../../../work_queue/WorkQueueNameCell.jsx';
import TooltipWithRef from '../../../common/Tooltip.jsx';
import { useWidgetAutoHeight } from '../../hooks/useWidgetAutoHeight.js';
import { useGridRequiredHeight } from '../../hooks/useGridTargetHeight.js';
import { work_queue_list_json_path } from '~/routes';
import WorkQueueCountCell from '../../../work_queue/WorkQueueCountCell.jsx';
import { gridThemeNoBorder } from '../../../common/AgGrid/gridTheme.js';
import styles from './MyWorkQueuesWidget.module.scss';

const CONDENSED_ROW_HEIGHT = 30;
const CONDENSED_HEADER_HEIGHT = 40;
const ROW_HEIGHT = 64;
const HEADER_HEIGHT = 48;

const VERTICAL_CENTER_STYLE = {
  display: 'flex',
  alignItems: 'center'
};

const buildColumnDefs = (condensedMode) => [
  {
    headerName: I18n.t('name'),
    field: 'name',
    flex: 2,
    minWidth: 200,
    cellRenderer: WorkQueueNameCell,
    cellRendererParams: {
      hideMyQueue: true
    },
    getQuickFilterText: ({ data }) => `${data.name} ${data.description || ''}`,
  },
  {
    headerName: I18n.t('unassigned'),
    field: 'unassigned_count',
    width: 130,
    cellStyle: VERTICAL_CENTER_STYLE,
    ...(!condensedMode ? {cellRenderer: WorkQueueCountCell} : {})
  },
  {
    headerName: I18n.t('work_queue_assigned'),
    field: 'in_progress_count',
    width: 130,
    cellStyle: VERTICAL_CENTER_STYLE,
    ...(!condensedMode ? {cellRenderer: WorkQueueCountCell} : {})
  },
  {
    headerName: I18n.t('work_queue_oldest_unassigned'),
    field: 'oldest_unassigned_at',
    width: 180,
    cellStyle: VERTICAL_CENTER_STYLE,
    cellRenderer: ({ value }) => {
      if (!value) return null;

      return (
        <TooltipWithRef
          onClick={e => e.stopPropagation()}
          title={DT.dt(value, { format: null, datetime: true })}
          trigger='hover'
        >
          <span>{dayjs.utc(value).local().startOf('second').locale(I18n.locale).fromNow()}</span>
        </TooltipWithRef>
      );
    },
  }
];

function MyWorkQueuesWidget({ widgetId, editable, theme, widgetRef }) {
  const { workQueueIds, listMode, condensedMode } = useSelector(state => state.dashboard.widgets[widgetId].settings);
  const { setTargetHeight } = useWidgetAutoHeight({ widgetId, editable });
  const gridRef = useRef(null);
  const { setRequiredGridHeight } = useGridRequiredHeight({ gridRef, widgetRef, theme, setTargetHeight });
  
  const columnDefs = useMemo(() => buildColumnDefs(condensedMode), []);

  const rowHeight = condensedMode ? CONDENSED_ROW_HEIGHT : ROW_HEIGHT;
  const headerHeight = condensedMode ? CONDENSED_HEADER_HEIGHT : HEADER_HEIGHT;

  const gridOptions = useMemo(() => ({
    rowHeight: rowHeight,
    headerHeight: headerHeight,
    overlayNoRowsTemplate: I18n.t('work_queue_empty_list'),
    onFirstDataRendered: () => setRequiredGridHeight(),
    domLayout: 'autoHeight'
  }), [condensedMode]);

  useEffect(() => {
    setRequiredGridHeight();  
  }, [rowHeight, headerHeight]);

  const handleSearch = (e) => {
    gridRef.current?.api?.setGridOption('quickFilterText', e.target.value);
  };

  return (
    <div className={styles['my-work-queues-widget']}>
      <CommonAggridClientSideTable
        gridRef={gridRef}
        baseKey='workQueueList'
        fetchPath={work_queue_list_json_path()}
        fetchBody={{
          only_own: true,
          work_queue_ids: listMode === 'chosen_queues' ? workQueueIds || [] : null
        }}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        containerStyle={{paddingBottom: 0, height: null}}
        theme={gridThemeNoBorder.withParams({autoHeightMinBodyHeight: 0})}
      />
    </div>
  )
}

export default MyWorkQueuesWidget;
